import React, { useState, useEffect } from "react";
import "./ServiceProvider.css";
import community_icon from "../../../images/Oscar.svg";
import {
  getVendorsByType,
  postVendorDetails,
} from "../../../Services/Vendor/VendorApi/VendorApi";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Modal from "react-bootstrap/Modal";
import ReactCrop from "react-easy-crop";

const getCroppedImg = (imageSrc, pixelCrop) => {
  const image = new Image();
  image.src = imageSrc;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Setting canvas size
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  return new Promise((resolve) => {
    image.onload = () => {
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
      // Resolve with the base64 image
      resolve(canvas.toDataURL("image/jpeg"));
    };
  });
};

const ServiceProvider = () => {
  const dispatch = useDispatch();
  const userId = localStorage.getItem("user_id");
  const vendors = useSelector((state) => state.getVendorsByType.data);
  const [vendorData, setVendorData] = useState([]);
  const [errors, setErrors] = useState({});
  const [initialVendorData, setInitialVendorData] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [image, setImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const handleCloseCropModal = () => {
    setShowCropModal(false);
    setImage(null);
    setSelectedIndex(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  useEffect(() => {
    if (Array.isArray(vendors?.vendor)) {
      const formattedVendors = vendors.vendor.map((v) => ({
        sub_vendor_id: v?.id || "",
        sub_vendor_type: v?.sub_vendor_type || "",
        vendor_detail_id: v?.vendor_detail_id || "",
        vendor_image: v?.vendor_image || "",
        name: v?.name || "",
        address: v?.address || "",
        email: v?.email || "",
        mobile: v?.mobile || "",
        state: v?.state || "",
        city: v?.city || "",
        website: v?.website || "",
      }));

      setVendorData(formattedVendors);
      setInitialVendorData(formattedVendors); // Save initial state
    } else {
      setVendorData([]);
      setInitialVendorData([]);
    }
  }, [vendors]);

  useEffect(() => {
    if (Array.isArray(vendors?.vendor)) {
      setVendorData(
        vendors.vendor.map((v) => ({
          sub_vendor_id: v?.id || "",
          sub_vendor_type: v?.sub_vendor_type || "",
          vendor_detail_id: v?.vendor_detail_id || "",
          vendor_image: v?.vendor_image || "",
          name: v?.name || "",
          address: v?.address || "",
          email: v?.email || "",
          mobile: v?.mobile || "",
          state: v?.state || "",
          city: v?.city || "",
          website: v?.website || "",
        }))
      );
    } else {
      setVendorData([]); // Reset to avoid errors
    }
  }, [vendors]);

  useEffect(() => {
    dispatch(
      getVendorsByType({
        vendor_master_name: "community_expert",
        user_id: userId,
      })
    );
  }, [dispatch, userId]);

  const handlePhoneNumberChange = (index, e) => {
    let name = e.target.name;
    let value = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
    let error = "";

    if (value.length > 10) {
      value = value.slice(0, 10); // Limit to 10 digits
    }

    // Format the phone number
    if (value.length <= 3) {
      // No formatting needed for values with length <= 3
    } else if (value.length <= 6) {
      value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    } else {
      value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(
        6,
        10
      )}`;
    }

    // Validate phone number format
    if (name === "mobile" && value.replace(/\D/g, "").length !== 10) {
      error = "Mobile number must be exactly 10 digits";
    }

    // Update state
    setVendorData((prev) =>
      prev.map((vendor, i) =>
        i === index ? { ...vendor, mobile: value } : vendor
      )
    );

    // Update errors state
    setErrors((prevErrors) => ({
      ...prevErrors,
      [`mobile-${index}`]: error,
    }));
  };

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    let updatedValue = value;
    let error = "";

    // Format validations (not mandatory)
    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        error = "Invalid email format";
      }
    }

    if (name === "website") {
      const websiteRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/;
      if (value && !websiteRegex.test(value)) {
        error = "Invalid website URL";
      }
    }

    setVendorData((prev) =>
      prev.map((vendor, i) =>
        i === index ? { ...vendor, [name]: updatedValue } : vendor
      )
    );

    setErrors((prevErrors) => ({
      ...prevErrors,
      [`${name}-${index}`]: error, // Store errors uniquely for each input
    }));
  };

  const handleImageChange = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
        setSelectedIndex(index);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file); // This reads file as base64
    }
  };

  const handleSave = async () => {
    try {
      if (!croppedAreaPixels || !image) return;

      const croppedImage = await getCroppedImg(image, croppedAreaPixels);

      setVendorData((prev) => {
        const updatedVendorData = [...prev];
        updatedVendorData[selectedIndex] = {
          ...updatedVendorData[selectedIndex],
          vendor_image: croppedImage,
          imagePreview: croppedImage,
        };
        return updatedVendorData;
      });

      handleCloseCropModal();
      setImage(null);
      setSelectedIndex(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (error) {
      console.error("Error cropping image:", error);
      toast.error("Failed to crop image");
    }
  };

  const isDataChanged = () => {
    return JSON.stringify(vendorData) !== JSON.stringify(initialVendorData);
  };

  const handleSubmit = () => {
    setIsSaving(true);
    if (!isDataChanged()) {
      setIsSaving(false);
      toast.info("No changes detected!");
      return;
    }
    const vendorDetails = vendorData.map((vendor) => {
      const { imagePreview, ...vendorData } = vendor;
      return {
        ...vendorData,
        // sub_vendor_id: vendor.id,
      };
    });

    // Construct the final payload
    const payload = {
      user_id: userId,
      vendor_id: vendors?.vendor_master?.id || "",
      vendor_details: vendorDetails,
    };

    // Send the JSON data
    dispatch(postVendorDetails(payload))
      .unwrap()
      .then((res) => {
        if (res?.status === 201) toast.success(res?.message);
        setIsSaving(false);
      })
      .catch((err) => {
        toast.error(err?.message);
        setIsSaving(false);
      });
  };

  return (
    <div>
      <div className="realtors-section community-section">
        <div className="realtors-inner-block">
          <div className="title-header d-flex justify-content-between align-items-center">
            <div className="community-heading">
              Real Estate Service Providers
            </div>
            <button
              className="orange-filled-btn addcommunitybtn save-btn-realtor"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
          {vendorData?.map((provider, index) => (
            <div
              className="all-commmunities-table"
              key={provider?.sub_vendor_id}
            >
              <div className="realtors-info-box d-flex gap-3 align-items-center">
                <div className="img-user">
                  <div className="image-container">
                    {provider.imagePreview ? (
                      <img
                        src={provider.imagePreview}
                        alt="Profile"
                        className="logo"
                      />
                    ) : (
                      <img
                        src={vendorData[index]?.vendor_image || community_icon}
                        alt="Profile"
                        className="logo"
                      />
                    )}
                    <label className="edit-icon" aria-label="Edit Image">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        width={32}
                        height={32}
                      >
                        <path d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712ZM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32l8.4-8.4Z" />
                        <path d="M5.25 5.25a3 3 0 0 0-3 3v10.5a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3V13.5a.75.75 0 0 0-1.5 0v5.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5V8.25a1.5 1.5 0 0 1 1.5-1.5h5.25a.75.75 0 0 0 0-1.5H5.25Z" />
                      </svg>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, index)} // Index for vendors
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>
                </div>
                <div className="form-info flex-grow-1">
                  <div className="realtors-name mb-2">
                    {vendorData[index]?.sub_vendor_type}
                  </div>
                  <form>
                    <div className="mb-3">
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        placeholder="Enter Name"
                        value={vendorData[index]?.name || ""}
                        onChange={(e) => handleChange(index, e)}
                      />
                    </div>
                    <div className="mb-3">
                      <input
                        type="text"
                        name="address"
                        className="form-control"
                        placeholder="Address"
                        value={vendorData[index]?.address || ""}
                        onChange={(e) => handleChange(index, e)}
                      />
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <input
                          type="text"
                          name="mobile"
                          className="form-control"
                          placeholder="Telephone"
                          value={vendorData[index]?.mobile || ""}
                          onChange={(e) => handlePhoneNumberChange(index, e)}
                        />
                        {errors[`mobile-${index}`] && (
                          <div className="text-danger">
                            {errors[`mobile-${index}`]}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 mb-3">
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          placeholder="Email"
                          value={vendorData[index]?.email || ""}
                          onChange={(e) => handleChange(index, e)}
                        />
                        {errors[`email-${index}`] && (
                          <div className="text-danger">
                            {errors[`email-${index}`]}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mb-3">
                      <input
                        type="text"
                        name="website"
                        className="form-control"
                        placeholder="Enter Website"
                        value={vendorData[index]?.website || ""}
                        onChange={(e) => handleChange(index, e)}
                      />
                      {errors[`website-${index}`] && (
                        <div className="text-danger">
                          {errors[`website-${index}`]}
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Modal show={showCropModal} onHide={handleCloseCropModal}>
        <Modal.Header closeButton>
          <Modal.Title>Crop Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ position: "relative", width: "100%", height: "300px" }}>
            {image && (
              <ReactCrop
                image={image}
                crop={crop}
                zoom={zoom}
                minWidth={400}
                minHeight={400}
                aspect={1}
                cropShape="round"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(croppedArea, croppedAreaPixels) => {
                  setCroppedAreaPixels(croppedAreaPixels);
                }}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="bttn-close" onClick={handleCloseCropModal}>
            Close
          </button>
          <button
            className="bttn-upload"
            onClick={handleSave}
            disabled={isSaving || !croppedAreaPixels}
          >
            Save Cropped Image
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ServiceProvider;
